import { createContext, useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { StoreContext } from '../store';
import { Box, Typography } from '@material-ui/core';
import { BlockOutlined } from '@material-ui/icons';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

// Permission context for better performance
const PermissionContext = createContext({
  permissions: [],
  hasPermission: () => false,
  isLoading: false,
});

// Provider component that fetches permissions once at app level
export const PermissionProvider = observer(({ children }) => {
  const store = useContext(StoreContext);
  const [isLoading, setIsLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    const initializePermissions = async () => {
      if (!store?.user?.role) {
        return;
      };

      try {
        setIsLoading(true);
        await store.rbac.getPermissions();
        // Direct access to avoid toJS conversion overhead
        setPermissions([...store.rbac.permissions]);
      } catch (error) {
        // console.error('Failed to initialize permissions:', error);
        setPermissions([]);
      } finally {
        setIsLoading(false);
      }
    };

    initializePermissions();
  }, [store?.user?.role]);

  const hasPermission = (permission) => permissions.includes(permission);
  const hasAnyPermission = (requiredPermissions) => 
    requiredPermissions.some(permission => permissions.includes(permission));
  const hasAllPermissions = (requiredPermissions) => 
    requiredPermissions.every(permission => permissions.includes(permission));
  const defaultRedirectTo = store?.rbac?.getFirstAccessibleMenu?.();

  const value = {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isLoading,
    defaultRedirectTo,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
});

// Lightweight hook that uses context instead of direct store access
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return context;
};

// Ultra-fast permission checker hook
export const useHasPermission = (permission) => {
  const { hasPermission } = usePermissions();
  return hasPermission(permission);
};

export const useHasAnyPermission = (permissions) => {
  if (!Array.isArray(permissions)) {
    permissions = [permissions];
  }

  const { hasAnyPermission } = usePermissions();
  return hasAnyPermission(permissions);
};

export const PermissionGate = ({ 
  permission, 
  permissions, 
  requireAll = false, 
  children, 
  fallback = null, 
  redirectTo,
  immediateRedirect = false
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading, defaultRedirectTo } = usePermissions();
  const router = useRouter();

  if (isLoading) {
    return null; // or a loading spinner
  }

  if (isLoading) {
    return null; // or a loading spinner
  }

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions && Array.isArray(permissions)) {
    hasAccess = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  } else {
    hasAccess = true;
  }

  if (!redirectTo && !defaultRedirectTo) {
    return null; // No redirect target specified
  } else if (!redirectTo && defaultRedirectTo && !hasAccess && immediateRedirect) {
    router.push(defaultRedirectTo.pathname || '/dashboard');
    return null; // Redirecting to default target
  }

  if (!fallback) {
    fallback = () => {
      setTimeout(() => {
        const target = redirectTo || defaultRedirectTo?.pathname || '/dashboard';
        if (router.pathname !== target) {
          router.push(target);
        }
      }, 2000);

      return (
        <Box 
          display="flex" 
          flexDirection="column"
          justifyContent="center" 
          alignItems="center" 
          minHeight={{ xs: '120px', sm: '200px' }}
          gap={2}
          boxShadow={3}
          width={{ xs: '90%', sm: '60%', md: '50%' }}
          margin="auto"
          marginY={{ xs: 6, sm: 12 }}
          paddingY={{ xs: 2, sm: 4 }}
          paddingX={{ xs: 3, sm: 6 }}
          textAlign={ 'center'}
        >
          <BlockOutlined style={{ fontSize: 48, color: '#f44336', marginBottom: '16px' }} />
          <Typography style={{ color: '#f44336', fontWeight: 600 }} variant="h6">{`Access Denied`}</Typography>
          <Typography style={{ fontWeight: 600, marginTop: 5 }} variant="body2">{`You do not have permission to access this page.`}</Typography>
          <Typography variant="body2" style={{ marginTop: 5, marginBottom: 20 }}>Redirect to {defaultRedirectTo?.labelId || 'dashboard'}...</Typography>
        </Box>
      );
    };
  }

  return hasAccess ? children : (typeof fallback === 'function' ? fallback() : fallback);
};

export const PermissionGuard = ({ permission, permissions, requireAll = false, children}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions && Array.isArray(permissions)) {
    hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
  } else {
    hasAccess = true;
  }

  return hasAccess ? children : null;
};
